import { CampaignStatus, FilterTypes, isLiveStatus } from "@madhive/mad-sdk";
import { madSDK } from "lib/sdk";
import { RootCampaign, ShallowLineItem } from "campaign-types";
import { RawInstructionStatus } from "features/ManageCampaigns/constants";

export const LEGACY_PRODUCT_SET_LIVE_MESSAGE =
  "One or more line items can't be set live — they use a legacy product. Update those line items or unset them and try again.";

export const wouldLineItemDeriveToLive = (input: {
  rawStatus?: number;
  instructionStatus?: number;
  startDate: Date | string | undefined;
  endDate: Date | string | undefined;
  now?: number;
}): boolean => {
  const status = input.rawStatus ?? input.instructionStatus;
  if (status !== RawInstructionStatus.DELIVERABLE) {
    return false;
  }
  if (!input.startDate || !input.endDate) {
    return false;
  }
  const now = input.now ?? Date.now();
  const start = new Date(input.startDate).valueOf();
  const end = new Date(input.endDate).valueOf();
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return false;
  }
  return start <= now && now <= end;
};

// V2 org blocks the PAUSED→LIVE transition when the line item is anchored to a V1 legacy product. Already-live V1 line items stay editable — the guard only fires on activation.
export const isLegacyProductBeingSetLive = (input: {
  currentStatus: CampaignStatus | undefined;
  nextStatus: CampaignStatus | undefined;
  productIsLegacy: boolean | undefined;
  newBillingEnabled: boolean;
}): boolean => {
  if (!input.newBillingEnabled) {
    return false;
  }
  if (input.productIsLegacy !== true) {
    return false;
  }
  if (!input.nextStatus || !isLiveStatus(input.nextStatus)) {
    return false;
  }
  if (input.currentStatus && isLiveStatus(input.currentStatus)) {
    return false;
  }
  return true;
};

export const anyLegacyLineItemBeingSetLive = async (
  lineItems: Array<{
    status?: CampaignStatus;
    product?: string;
    productId?: string;
    rawStatus?: number;
    instructionStatus?: number;
    startDate?: Date | string;
    endDate?: Date | string;
    isExplicitlySetLive?: boolean;
  }>,
  newBillingEnabled: boolean,
  nextStatus: CampaignStatus = CampaignStatus.LIVE
): Promise<boolean> => {
  if (!newBillingEnabled) {
    return false;
  }
  const now = Date.now();
  const fallbackWantsLive = isLiveStatus(nextStatus);

  const candidates = lineItems.filter(li => {
    const productRef = li.productId || li.product;
    if (!productRef) {
      return false;
    }
    if (li.status && isLiveStatus(li.status)) {
      return false;
    }
    const wantsExplicit = li.isExplicitlySetLive ?? fallbackWantsLive;
    return (
      wantsExplicit ||
      wouldLineItemDeriveToLive({
        rawStatus: li.rawStatus,
        instructionStatus: li.instructionStatus,
        startDate: li.startDate,
        endDate: li.endDate,
        now
      })
    );
  });
  if (candidates.length === 0) {
    return false;
  }

  const legacyProducts = await madSDK.products.find({
    where: [
      { field: "legacyFilter", type: FilterTypes.EQ, value: "LEGACY_ONLY" }
    ]
  });
  const legacyProductIds = new Set(legacyProducts.map(p => p.id));

  return candidates.some(li => {
    const productRef = (li.productId || li.product) as string;
    return isLegacyProductBeingSetLive({
      currentStatus: li.status,
      nextStatus: CampaignStatus.LIVE,
      productIsLegacy: legacyProductIds.has(productRef),
      newBillingEnabled: true
    });
  });
};

type BulkEditForCollector = {
  status?: RawInstructionStatus;
  startDate?: Date | string;
  endDate?: Date | string;
};

export type CollectedSetLiveLineItem = ShallowLineItem & {
  isExplicitlySetLive: boolean;
};

export const collectSetLiveLineItems = (input: {
  lineItemEdits: Record<string, BulkEditForCollector> | undefined;
  campaignEdits: Record<string, { status?: RawInstructionStatus }> | undefined;
  allLineItems: Record<string, ShallowLineItem | undefined>;
  allCampaigns: Record<string, RootCampaign | undefined>;
}): CollectedSetLiveLineItem[] => {
  const relevantLineItems: CollectedSetLiveLineItem[] = [];

  Object.entries(input.lineItemEdits ?? {}).forEach(([id, edit]) => {
    if (!edit) {
      return;
    }
    const isSetLiveEdit = edit.status === RawInstructionStatus.DELIVERABLE;
    const isDateEdit =
      edit.startDate !== undefined || edit.endDate !== undefined;
    if (!isSetLiveEdit && !isDateEdit) {
      return;
    }
    const li = input.allLineItems[id];
    if (!li) {
      return;
    }
    relevantLineItems.push({
      ...li,
      ...(edit.startDate !== undefined && {
        startDate: new Date(edit.startDate)
      }),
      ...(edit.endDate !== undefined && { endDate: new Date(edit.endDate) }),
      isExplicitlySetLive: isSetLiveEdit
    });
  });

  Object.entries(input.campaignEdits ?? {}).forEach(([id, edit]) => {
    if (edit?.status !== RawInstructionStatus.DELIVERABLE) {
      return;
    }
    const campaign = input.allCampaigns[id];
    (campaign?.lineItems ?? [])
      .filter(li => li.userCanSetLive)
      .forEach(li =>
        relevantLineItems.push({ ...li, isExplicitlySetLive: true })
      );
  });

  return relevantLineItems;
};
